import type { StreamObserver } from "./observer";
import type { OakStreamInternalOptions, OakStreamState } from "./types";

import { createInitialStreamState } from "./state";
import { streamSideSideEventsIterator } from "./streamIterator";
import { traceOakStream } from "./trace";

interface StreamConfiguration {
  hash: string;
  url: string;
  getIdTokenRefreshIfExpired: OakStreamInternalOptions["getIdTokenRefreshIfExpired"];
  initialLastEventId?: string;
  removeFromCache: (hash: string) => void;
}

interface StreamRuntime {
  connectionPromise: Promise<void> | null;
  startPromise: Promise<void> | null;
  abortController: AbortController | null;
  observers: Set<StreamObserver>;
  gcTimeout: ReturnType<typeof setTimeout> | null;
  /**
   * Monotonic generation counter for the underlying network connection owned by
   * this cached `Stream` instance.
   *
   * The stream hash guarantees that observers with the same inputs share one
   * `Stream` object, but that object can still create multiple sequential fetches
   * over its lifetime. For example, when the last observer unmounts we abort the
   * active SSE request but keep the `Stream` in cache for a short period. If a
   * new observer mounts before the aborted request has fully unwound, the same
   * `Stream` instance can start a new fetch while the previous one is still
   * resolving its `catch`/`finally` path.
   *
   * `connectionId` lets us treat each fetch attempt as its own generation and
   * ignore late state updates from older generations. Without it, an older
   * aborted connection could incorrectly overwrite the status, emit an error, or
   * clear runtime bookkeeping that belongs to the newer active connection.
   */
  connectionId: number;
  isClosed: boolean;
}

const DEFAULT_GC_TIME = 5 * 60 * 1000;

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export class Stream {
  private url: string;
  private getIdTokenRefreshIfExpired: OakStreamInternalOptions["getIdTokenRefreshIfExpired"];
  private state: OakStreamState;
  private readonly removeFromCache: (hash: string) => void;
  private readonly runtime: StreamRuntime = {
    connectionPromise: null,
    startPromise: null,
    abortController: null,
    observers: new Set(),
    gcTimeout: null,
    connectionId: 0,
    isClosed: false,
  };

  readonly hash: string;

  constructor(configuration: StreamConfiguration) {
    this.hash = configuration.hash;
    this.url = configuration.url;
    this.getIdTokenRefreshIfExpired = configuration.getIdTokenRefreshIfExpired;
    this.state = createInitialStreamState(configuration.initialLastEventId);
    this.removeFromCache = configuration.removeFromCache;
  }

  getUrl() {
    return this.url;
  }

  getState() {
    return this.state;
  }

  setState(nextState: OakStreamState) {
    this.state = nextState;
    this.notifyObservers();
  }

  addObserver(observer: StreamObserver) {
    if (this.runtime.isClosed) {
      return;
    }

    this.runtime.observers.add(observer);
    traceOakStream("stream", "addObserver", {
      url: this.url,
      hash: this.hash,
      observerCount: this.runtime.observers.size,
      lastEventId: this.state.lastEventId,
      status: this.state.status,
    });
    this.clearGcTimeout();
  }

  removeObserver(observer: StreamObserver) {
    this.runtime.observers.delete(observer);
    traceOakStream("stream", "removeObserver", {
      url: this.url,
      hash: this.hash,
      observerCount: this.runtime.observers.size,
      lastEventId: this.state.lastEventId,
      status: this.state.status,
    });

    if (this.runtime.isClosed) {
      return;
    }

    if (this.runtime.observers.size === 0) {
      if (this.runtime.abortController !== null) {
        traceOakStream("stream", "abortForNoObservers", {
          url: this.url,
          hash: this.hash,
          lastEventId: this.state.lastEventId,
        });
        this.runtime.abortController.abort();
      }

      this.setState({
        ...this.state,
        status: "closed",
        error: null,
      });
      this.scheduleGc();
    }
  }

  connect() {
    if (this.runtime.isClosed) {
      return Promise.resolve();
    }

    if (
      this.runtime.connectionPromise !== null &&
      this.runtime.abortController?.signal.aborted !== true
    ) {
      return this.runtime.startPromise ?? Promise.resolve();
    }

    return this.startConnection(false);
  }

  restart() {
    if (this.runtime.isClosed) {
      return Promise.resolve();
    }

    return this.startConnection(true);
  }

  close() {
    if (this.runtime.isClosed) {
      return;
    }

    traceOakStream("stream", "close", {
      url: this.url,
      hash: this.hash,
      lastEventId: this.state.lastEventId,
      status: this.state.status,
    });
    this.runtime.isClosed = true;
    this.runtime.connectionId += 1;
    this.clearGcTimeout();

    if (this.runtime.abortController !== null) {
      this.runtime.abortController.abort();
    }

    this.runtime.connectionPromise = null;
    this.runtime.startPromise = null;
    this.runtime.abortController = null;
    this.runtime.observers.clear();
    this.state = {
      ...this.state,
      status: "closed",
      error: null,
    };

    this.removeFromCache(this.hash);
  }

  private startConnection(force: boolean) {
    if (this.runtime.isClosed) {
      return Promise.resolve();
    }

    if (
      !force &&
      this.runtime.connectionPromise !== null &&
      this.runtime.abortController?.signal.aborted !== true
    ) {
      return this.runtime.startPromise ?? Promise.resolve();
    }

    if (force && this.runtime.abortController !== null) {
      traceOakStream("stream", "abortForRestart", {
        url: this.url,
        hash: this.hash,
        lastEventId: this.state.lastEventId,
      });
      this.runtime.abortController.abort();
    }

    const nextConnectionId = this.runtime.connectionId + 1;
    const abortController = new AbortController();
    let resolveStarted: (() => void) | undefined;
    const startPromise = new Promise<void>((resolve) => {
      resolveStarted = resolve;
    });

    this.runtime.connectionId = nextConnectionId;
    this.runtime.startPromise = startPromise;
    this.runtime.abortController = abortController;
    traceOakStream("stream", "startConnection", {
      url: this.url,
      hash: this.hash,
      force,
      connectionId: nextConnectionId,
      lastEventId: this.state.lastEventId,
      observerCount: this.runtime.observers.size,
    });
    this.setState({
      ...this.state,
      status: "connecting",
      error: null,
    });

    const connectionPromise = streamSideSideEventsIterator({
      getIdTokenRefreshIfExpired: this.getIdTokenRefreshIfExpired,
      lastEventId: this.state.lastEventId,
      signal: abortController.signal,
      url: this.url,
    })
      .then(async (stream) => {
        if (this.runtime.connectionId !== nextConnectionId || abortController.signal.aborted) {
          resolveStarted?.();
          return;
        }

        traceOakStream("stream", "connected", {
          url: this.url,
          hash: this.hash,
          connectionId: nextConnectionId,
          lastEventId: this.state.lastEventId,
        });
        this.setState({
          ...this.state,
          status: "connected",
          error: null,
        });
        resolveStarted?.();
        resolveStarted = undefined;

        for await (const event of stream) {
          if (this.runtime.connectionId !== nextConnectionId || abortController.signal.aborted) {
            return;
          }

          traceOakStream("stream", "event", {
            url: this.url,
            hash: this.hash,
            connectionId: nextConnectionId,
            eventId: event.id,
            eventName: event.event,
          });
          this.setState({
            ...this.state,
            status: "connected",
            data: [...this.state.data, event],
            error: null,
            lastEventId: event.id,
            updatedAt: Date.now(),
          });
        }

        if (this.runtime.connectionId === nextConnectionId) {
          traceOakStream("stream", "iteratorClosed", {
            url: this.url,
            hash: this.hash,
            connectionId: nextConnectionId,
            lastEventId: this.state.lastEventId,
          });
          this.setState({
            ...this.state,
            status: "closed",
            error: null,
            updatedAt: this.state.updatedAt || Date.now(),
          });
        }
      })
      .catch((error: unknown) => {
        if (this.runtime.connectionId !== nextConnectionId) {
          resolveStarted?.();
          return;
        }

        if (abortController.signal.aborted || isAbortError(error)) {
          resolveStarted?.();
          resolveStarted = undefined;
          traceOakStream("stream", "aborted", {
            url: this.url,
            hash: this.hash,
            connectionId: nextConnectionId,
            lastEventId: this.state.lastEventId,
          });
          this.setState({
            ...this.state,
            status: "closed",
            error: null,
          });
          return;
        }

        const normalizedError =
          error instanceof Error ? error : new Error(String(error ?? "Stream failed."));

        resolveStarted?.();
        resolveStarted = undefined;
        traceOakStream("stream", "error", {
          url: this.url,
          hash: this.hash,
          connectionId: nextConnectionId,
          message: normalizedError.message,
        });
        this.setState({
          ...this.state,
          status: "error",
          error: normalizedError,
          updatedAt: Date.now(),
        });
      })
      .finally(() => {
        traceOakStream("stream", "connectionFinally", {
          url: this.url,
          hash: this.hash,
          connectionId: nextConnectionId,
          observerCount: this.runtime.observers.size,
          isClosed: this.runtime.isClosed,
        });
        if (this.runtime.isClosed) {
          return;
        }

        if (
          this.runtime.connectionId === nextConnectionId &&
          this.runtime.connectionPromise === connectionPromise
        ) {
          this.runtime.connectionPromise = null;
          this.runtime.startPromise = null;
          this.runtime.abortController = null;
        }

        if (this.runtime.observers.size === 0) {
          this.scheduleGc();
        }
      });

    this.runtime.connectionPromise = connectionPromise;

    return startPromise;
  }

  private notifyObservers() {
    for (const observer of this.runtime.observers) {
      observer.onStreamUpdated();
    }
  }

  private clearGcTimeout() {
    if (this.runtime.gcTimeout !== null) {
      clearTimeout(this.runtime.gcTimeout);
      this.runtime.gcTimeout = null;
    }
  }

  private scheduleGc() {
    if (this.runtime.isClosed) {
      return;
    }

    this.clearGcTimeout();

    this.runtime.gcTimeout = setTimeout(() => {
      if (this.runtime.observers.size > 0) {
        return;
      }

      if (this.runtime.connectionPromise !== null) {
        this.scheduleGc();
        return;
      }

      this.removeFromCache(this.hash);
    }, DEFAULT_GC_TIME);
  }
}
